import analytics from '../../utils/analytics';
import camelCase from 'lodash/camelCase';
import {
    resolveAnalyticsUrl,
    extractContentfulEntryId
} from '../../utils/content-analytics-resources';
import { type ParentPageType } from '../../utils/parent-page-type';

/**
 * Required fields for tracking content link analytics events.
 * All fields are mandatory when providing analytics data.
 */
export interface ContentLinkAnalyticsPayload {
    /** The type of link being clicked ('card' or 'text-link') */
    linkType: 'card' | 'text-link';
    /** The context where the link appears. */
    context:
        | 'search-results'
        | 'sidebar'
        | 'browse'
        | 'reference'
        | 'featured-content';
    /** The position/index of the link in the list (0-based) */
    position: number;
    /** The content ARI of the linked content (optional, undefined if not available) */
    linkContentAri?: string;
    /** This content ID of the linked content (optional, may be derived from contentAri if available. */
    linkContentId?: string;
    /** The resolved URL of the linked content */
    linkContentResolvedUrl: string;
    /** The type of parent page (optional, can be null for home/root pages) */
    parentPageType?: ParentPageType;
}
export interface ContentLinkAnalytics {
    payload?: ContentLinkAnalyticsPayload;
    /** Should skip the submission of the content analytics */
    shouldSkip: boolean;
}

/**
 * Type representing the event type for content link analytics
 * - 'clicked': User clicked on the link
 * - 'viewed': Link was displayed to the user
 */
type ContentLinkEventType = 'clicked' | 'viewed';

/**
 * Parameters for building content link analytics properties
 */
interface BuildContentLinkAnalyticsParams {
    /** The analytics payload containing link metadata */
    payload: ContentLinkAnalyticsPayload;
    /** The type of event (clicked or viewed) */
    eventType: ContentLinkEventType;
    /** The href of the clicked link (only for clicked events) */
    href?: string | null;
}

/**
 * Builds enhanced analytics properties for content link events (clicks and views)
 * Handles both 'clicked' and 'viewed' event types with conditional property inclusion
 */
function buildContentLinkAnalytics({
    payload,
    eventType,
    href
}: BuildContentLinkAnalyticsParams) {
    const {
        linkType,
        context,
        position,
        linkContentAri,
        linkContentId,
        linkContentResolvedUrl,
        parentPageType
    } = payload;

    const linkContentEntryId =
        linkContentId || extractContentfulEntryId(linkContentAri) || undefined;
    const resolvedUrl = resolveAnalyticsUrl(linkContentResolvedUrl);

    return {
        event: eventType,
        eventComponent: 'content-link',
        eventContainer: context,
        position,
        link_type: linkType,
        link_content_ari: linkContentAri || undefined,
        link_content_entry_id: linkContentEntryId,
        link_content_resolved_url: resolvedUrl,
        ...(parentPageType && { parent_page_type: parentPageType }),
        ...(eventType === 'clicked' && href && { clickTarget: href })
    };
}
/**
 * Builds standard analytics properties for regular links
 */
function buildStandardAnalytics(href: string | null, name: string) {
    let target;
    let actionSubjectId;
    if (name.match(/.*button/i)) {
        target = 'button';
        actionSubjectId = camelCase(name.replace(/button/i, ''));
    } else if (name.match(/.*card/i)) {
        target = 'card';
        actionSubjectId = camelCase(name.replace(/card/i, ''));
    } else {
        target = 'link';
        actionSubjectId = camelCase(name);
    }
    return { target, actionSubjectId, clickTarget: href };
}

/**
 * Track a content link viewed event with type-safe payload
 */
export function trackContentLinkViewed(
    payload: ContentLinkAnalyticsPayload
): void {
    const enhancedProperties = buildContentLinkAnalytics({
        payload,
        eventType: 'viewed'
    });
    analytics.trackEvent('content-link viewed', enhancedProperties);
}

export function trackContentLinkClick(params: {
    href: string | null;
    text?: string;
    name?: string;
    contentLinkAnalytics?: ContentLinkAnalytics;
}) {
    const { href, text, name, contentLinkAnalytics } = params;

    // Only track content link analytics if payload exists and shouldSkip is false
    if (contentLinkAnalytics?.payload && !contentLinkAnalytics?.shouldSkip) {
        const enhancedProperties = buildContentLinkAnalytics({
            payload: contentLinkAnalytics.payload,
            eventType: 'clicked',
            href
        });

        if (name) {
            const { target, actionSubjectId } = buildStandardAnalytics(
                href,
                name
            );
            return analytics.trackEvent('content-link clicked', {
                ...enhancedProperties,
                actionSubjectId,
                target
            });
        }

        return analytics.trackEvent('content-link clicked', enhancedProperties);
    }
    if (name) {
        const { target, actionSubjectId, clickTarget } = buildStandardAnalytics(
            href,
            name
        );
        return analytics.processEvent(target, '', {
            actionSubjectId,
            clickTarget
        });
    }

    analytics.trackLinkClick(href, text);
}
// Backward compatible wrapper
const track = (href: string | null, text?: string, name?: string) => {
    trackContentLinkClick({ href, text, name });
};

export default track;
